import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateCustomTypes,
  FilterDocumentDateFilterTypes,
  FilterOptionKeys,
  GroupByMode,
  SortingMode,
  TaskGroupBy,
  TaskType,
  UserSettings,
} from '@griply/types';
import {
  addDays,
  addMonths,
  differenceInDays,
  endOfMonth,
  endOfYear,
  isFirstDayOfMonth,
  isSameMonth,
} from 'date-fns';

import { upcomingFilters } from './constants/upcoming-page-options';
import { pickFilterOptions } from './pick-filter-options';

/**
 * Computes the max date for the upcoming view.
 * This matches the logic in `useUpcomingSections`:
 * - 10 individual days
 * - RestOfMonth (if applicable)
 * - 10 monthly sections
 * - Rest of year
 */
const getUpcomingMaxDate = (today: Date): Date => {
  const restOfMonthStartDate = addDays(today, 10);
  const restOfMonthEndDate = endOfMonth(restOfMonthStartDate);

  const shouldHaveRestOfMonth =
    differenceInDays(restOfMonthEndDate, restOfMonthStartDate) >= 0 &&
    !isFirstDayOfMonth(restOfMonthStartDate);

  const monthCounter =
    shouldHaveRestOfMonth && !isSameMonth(restOfMonthStartDate, today) ? 1 : 0;

  return endOfYear(addMonths(today, monthCounter + 11));
};

export const buildUpcomingDefaultFilter = (today: Date): FilterDocument => {
  const maxDate = getUpcomingMaxDate(today);

  const scheduledBranch = {
    [FilterOptionKeys.Date]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: maxDate,
      },
    },
  };

  const deadlineBranch = {
    [FilterOptionKeys.Deadline]: {
      type: FilterDocumentDateFilterTypes.Custom,
      custom: {
        type: FilterDocumentDateCustomTypes.Before,
        start: null,
        end: maxDate,
      },
    },
  };

  return {
    [DocumentOrganizationKeys.GroupByMode]:
      GroupByMode.UpcomingDeadline as TaskGroupBy,
    [DocumentOrganizationKeys.SortingMode]: SortingMode.DueDate,
    [DocumentOrganizationKeys.Filters]: {
      [FilterOptionKeys.Type]: null,
      [FilterOptionKeys.Tag]: null,
    },
    [DocumentOrganizationKeys.Or]: [scheduledBranch, deadlineBranch],
  };
};

export const buildUpcomingFilterDocument = (
  today: Date,
  settings?: UserSettings,
): FilterDocument => {
  const baseDocument = buildUpcomingDefaultFilter(today);

  if (!settings) {
    return baseDocument;
  }

  const typeFilter = settings.upcoming?.hideHabits
    ? {
        include: Object.values(TaskType).filter(
          (type) => type !== TaskType.Habit,
        ),
      }
    : baseDocument[DocumentOrganizationKeys.Filters]?.[FilterOptionKeys.Type];

  const coerceSortingMode = (mode?: SortingMode | null): SortingMode | null =>
    mode && Object.values(SortingMode).includes(mode) ? mode : null;

  // Legacy fallback: before Upcoming owned its sort, reorders wrote todoSortingMode.
  const sortingMode =
    coerceSortingMode(settings.upcoming?.sortingMode) ??
    coerceSortingMode(settings.todoSortingMode) ??
    baseDocument[DocumentOrganizationKeys.SortingMode];

  const savedFilters =
    settings.filters?.upcoming?.[DocumentOrganizationKeys.Filters];

  return {
    ...baseDocument,
    [DocumentOrganizationKeys.SortingMode]: sortingMode,
    [DocumentOrganizationKeys.Filters]: {
      ...baseDocument[DocumentOrganizationKeys.Filters],
      [FilterOptionKeys.Type]: typeFilter,
      // Read back exactly what the Upcoming filter UI can set — nothing persisted outside that list reaches the calendar.
      ...pickFilterOptions(savedFilters, upcomingFilters),
    },
    [DocumentOrganizationKeys.ShowCompleted]: !settings.upcoming?.hideCompleted,
  };
};
